import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (c, cur = "USD") =>
  `${cur === "USD" ? "$" : cur + " "}${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicInvoice() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/invoice/${token}`)
      .then((r) => setInvoice(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Invoice not found."))
      .finally(() => setLoading(false));
  }, [token]);

  const pay = async () => {
    setPaying(true);
    try {
      const resp = await axios.post(`${API}/public/invoice/${token}/pay`, {
        origin_url: window.location.origin,
      });
      window.location.href = resp.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment setup failed.");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center" style={{ color: "var(--ink-soft)" }}>
        <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading invoice…
      </div>
    );
  }
  if (error || !invoice) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <AlertTriangle className="h-10 w-10 inline mb-3" style={{ color: "var(--terracotta)" }} />
        <h1 className="font-serif text-3xl">Invoice unavailable</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>{error}</p>
      </div>
    );
  }

  const paidQuery = params.get("paid");
  const isPaid = invoice.status === "paid" || paidQuery === "1";
  const vendor = invoice.vendor || {};

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10" data-testid="public-invoice-page">
      {isPaid && (
        <div
          data-testid="public-invoice-paid-banner"
          className="rounded-sm p-4 mb-6 flex items-center gap-3"
          style={{ background: "rgba(31,93,52,0.1)", borderLeft: "3px solid var(--moss)" }}
        >
          <CheckCircle2 className="h-5 w-5" style={{ color: "var(--moss)" }} />
          <div>
            <div className="font-semibold">Paid — thanks!</div>
            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
              {vendor.business_name || vendor.name} has been notified.
            </div>
          </div>
        </div>
      )}

      <div className="card-warm rounded-sm p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline" style={{ color: "var(--terracotta)" }}>
              Invoice {invoice.number}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1">{vendor.business_name || vendor.name}</h1>
            {vendor.location && <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>{vendor.location}</p>}
          </div>
          <span
            data-testid="public-invoice-status"
            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase"
            style={{
              background: invoice.status === "paid" ? "rgba(31,93,52,0.12)" : "rgba(249,115,22,0.12)",
              color: invoice.status === "paid" ? "var(--moss)" : "var(--gold)",
            }}
          >
            {invoice.status}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Bill to</div>
            <div className="mt-1 font-semibold">{invoice.bill_to?.name}</div>
            {invoice.bill_to?.email && <div style={{ color: "var(--ink-soft)" }}>{invoice.bill_to.email}</div>}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Issue date</div>
            <div className="mt-1">{invoice.issue_date || "—"}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Due</div>
            <div className="mt-1">{invoice.due_date || "On receipt"}</div>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--sand)" }}>
                <th className="text-left py-2 font-semibold">Description</th>
                <th className="text-right py-2 font-semibold">Qty</th>
                <th className="text-right py-2 font-semibold">Rate</th>
                <th className="text-right py-2 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b" style={{ borderColor: "var(--sand)" }}>
                  <td className="py-2">{li.description}</td>
                  <td className="py-2 text-right">{li.qty}</td>
                  <td className="py-2 text-right">{money(li.unit_amount_cents, invoice.currency)}</td>
                  <td className="py-2 text-right">{money((li.qty || 1) * (li.unit_amount_cents || 0), invoice.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 ml-auto max-w-xs text-sm">
          <div className="flex justify-between py-1">
            <span style={{ color: "var(--ink-soft)" }}>Subtotal</span>
            <span>{money(invoice.subtotal_cents, invoice.currency)}</span>
          </div>
          {invoice.discount_cents > 0 && (
            <div className="flex justify-between py-1">
              <span style={{ color: "var(--ink-soft)" }}>Discount</span>
              <span>-{money(invoice.discount_cents, invoice.currency)}</span>
            </div>
          )}
          {invoice.tax_cents > 0 && (
            <div className="flex justify-between py-1">
              <span style={{ color: "var(--ink-soft)" }}>Tax</span>
              <span>{money(invoice.tax_cents, invoice.currency)}</span>
            </div>
          )}
          <div
            className="flex justify-between py-2 text-lg font-semibold border-t mt-1"
            style={{ borderColor: "var(--terracotta)", color: "var(--terracotta)" }}
          >
            <span>Total</span>
            <span data-testid="public-invoice-total">{money(invoice.total_cents, invoice.currency)}</span>
          </div>
        </div>

        {invoice.notes && (
          <div className="mt-6 rounded-sm p-4 text-sm" style={{ background: "var(--sand)" }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--ink-soft)" }}>Notes</div>
            {invoice.notes}
          </div>
        )}

        {!isPaid && (
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {invoice.can_pay_online ? (
              <Button
                data-testid="public-invoice-pay-btn"
                className="btn-primary rounded-full px-6 py-3 inline-flex items-center gap-2"
                onClick={pay}
                disabled={paying}
              >
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Pay {money(invoice.total_cents, invoice.currency)} online
              </Button>
            ) : (
              <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                Online payment is not available for this invoice. Please contact{" "}
                <a href={`mailto:${vendor.email}`} style={{ color: "var(--terracotta)" }}>
                  {vendor.business_name || vendor.name}
                </a> to arrange payment.
              </div>
            )}
            <a
              data-testid="public-invoice-pdf"
              href={`${API}/public/invoice/${token}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm inline-flex items-center gap-1 underline"
              style={{ color: "var(--ink-soft)" }}
            >
              <Download className="h-4 w-4" /> Download PDF
            </a>
          </div>
        )}
      </div>

      <div className="text-center mt-6 text-xs" style={{ color: "var(--ink-soft)" }}>
        Powered by <span style={{ color: "var(--terracotta)" }}>AddVendTour</span>
      </div>
    </div>
  );
}
